import React, { useState, useContext, useEffect } from 'react';
import { Record } from 'immutable';
import { useSelector, useDispatch } from 'react-redux';
import uniqBy from 'lodash/uniqBy';
import reverse from 'lodash/reverse';

// Context
import WorkspaceActionCableContext from 'Shared/contexts/WorkspaceActionCableContext';

// Helpers
import WorkspaceMeetingModeHelper from 'Shared/model_helpers/WorkspaceMeetingMode.helper';

// Types
import { WorkspaceMeetingMode } from 'Shared/types/WorkspaceMeetingMode.type';

// Selectors
import * as meetingItemSelectors from 'Shared/selectors/MeetingItems.selectors';
import { getUserFlag } from 'Shared/selectors/user';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

// Components
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpTabs, WpTab } from 'Wpds/components/tabs';
import MeetingItemComments, {
  SortDirection,
} from 'Shared/components/MeetingItemDetailPanel/comments/MeetingItemComments';
import MeetingItemHistoryList from 'Shared/components/MeetingItemDetailPanel/history/MeetingItemHistoryList';
import { WpTooltip, WpTooltipTrigger, WpTooltipContent } from 'Wpds/components/tooltip';

// Styles
import './NotesPanel.scss';

const MEETING_MODE_COMMENTS_FLAG_NAME = 'meeting_mode_comment_direction';

type Props = {
  meetingModeSession: Record<WorkspaceMeetingMode>;
};

enum Tabs {
  notes = 'notes',
  history = 'history',
}

const NotesPanel = ({ meetingModeSession }: Props) => {
  const dispatch = useDispatch();
  const workspaceChannel = useContext(WorkspaceActionCableContext);

  const meetingModeCommentDirection =
    useSelector(state => getUserFlag(state, { flag: MEETING_MODE_COMMENTS_FLAG_NAME })) || SortDirection.OLDEST_FIRST;

  const [commentSortDirection, setCommentSortDirection] = useState(meetingModeCommentDirection);
  const [activeTab, setActiveTab] = useState(Tabs.notes);
  const [editingCommentsList, setEditingCommentsList] = useState([]);
  const meetingItemId = WorkspaceMeetingModeHelper.getWorkspaceMeetingItemUuid(meetingModeSession);
  const meetingModeSessionId = WorkspaceMeetingModeHelper.getId(meetingModeSession);

  const meetingItem = useSelector(state =>
    meetingItemSelectors.getMeetingItemByUuid(state, { meetingItemUuid: meetingItemId })
  ).get(meetingItemId);

  const handleUpdateEditingCommentsList = ({ uuid, isEditing }) => {
    setEditingCommentsList(uniqBy(reverse([...editingCommentsList, { uuid, isEditing }]), 'uuid'));
  };

  const isEditingComment = editingCommentsList.filter(comment => comment.isEditing === true).length > 0;

  const handleSwitchFilterDirection = () => {
    const newDirection =
      commentSortDirection === SortDirection.NEWEST_FIRST ? SortDirection.OLDEST_FIRST : SortDirection.NEWEST_FIRST;
    setCommentSortDirection(newDirection);

    dispatch(UsersActions.updateUserFlag(MEETING_MODE_COMMENTS_FLAG_NAME, newDirection));
  };

  useEffect(() => {
    let timer;
    const isTyping = isEditingComment;
    const updateChannel = () => {
      workspaceChannel.send({ event: 'user_typing', isTyping, meetingModeSessionId });

      if (isTyping) timer = setTimeout(updateChannel, 2000);
    };

    updateChannel();

    return () => clearTimeout(timer);
  }, [isEditingComment, meetingModeSessionId, workspaceChannel]);

  if (!meetingItem) return null;
  const baseClassName = 'NotesPanel';

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__header`}>
        <div className={`${baseClassName}__tabs`}>
          <WpTabs activeTab={activeTab} onTabClick={setActiveTab}>
            <WpTab tabId={Tabs.notes}>Previous Notes</WpTab>
            <WpTab tabId={Tabs.history}>History</WpTab>
          </WpTabs>
        </div>
        {activeTab === Tabs.notes && (
          <WpTooltip placement="bottom-end">
            <WpTooltipTrigger>
              <WpButton color="gray" size="sm" onClick={handleSwitchFilterDirection}>
                <WpIcon
                  name={`${
                    commentSortDirection === SortDirection.OLDEST_FIRST ? 'sort-amount-down' : 'sort-amount-up-alt'
                  }`}
                />
              </WpButton>
            </WpTooltipTrigger>
            <WpTooltipContent>
              Switch to {commentSortDirection === SortDirection.OLDEST_FIRST ? 'Newest First' : 'Oldest First'}
            </WpTooltipContent>
          </WpTooltip>
        )}
      </div>
      {activeTab === Tabs.notes && (
        <MeetingItemComments
          meetingItem={meetingItem}
          className={`${baseClassName}__list`}
          emptyStateComponent={
            <div className={`${baseClassName}__notes-empty`}>There are no previous notes for this topic</div>
          }
          setIsEditing={handleUpdateEditingCommentsList}
          sort={commentSortDirection}
        />
      )}
      {activeTab === Tabs.history && (
        <MeetingItemHistoryList meetingItem={meetingItem} className={`${baseClassName}__history-list`} />
      )}
    </div>
  );
};

export default NotesPanel;
